import { createContext, ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { publish, subscribe, unsubscribe } from '../../EventsBus';
import { MeetDomainEvents } from '../domain';
import { useTranslationsContext } from '../../Translations/providers/TranslationsContextProvider';
import { Connection, Features, Meet, MeetRepository, Participant } from '../domain/types';
import { capitalize } from '../../../utils/textTransform';
import { useSessionStoragePersistedState } from '../../SessionStorageState/hooks';
import { VideoCallEvents } from '../../VideoCall/domain/events';

const INSURANCE_BRANDS: Record<string, { primary: string; secondary: string, name: string }> | undefined = import.meta.env.INSURANCE_BRANDS
	? import.meta.env.INSURANCE_BRANDS as Record<string, { primary: string; secondary: string, name: string }>
	: globalThis.import_meta_env.INSURANCE_BRANDS as Record<string, { primary: string; secondary: string, name: string }>;

export interface MeetContextProviderProps {
	children: ReactElement;
	repository: MeetRepository;
	features?: Features;
}

export interface MeetContextValue {
	meet?: Meet;
	meetLink?: string;
	meetReadyForWaiting: boolean;
	features?: Features;
	isProfessional: boolean;
	patientData?: Participant;
	professionalData?: Participant;
	participantData?: Participant;
	specialty?: string;
	retrieveMeetError?: string;
	refreshMeetError?: string;
	professionalAvailable: boolean;
	connectionCredentials?: Connection;
	isCopiedMeetLinkToClipboard: boolean;
	participantsNumber: number;
	participantConnected: boolean;
	retrieveMeet: (code: string, tenant?: string) => void;
	refreshMeet: () => void;
	closeMeetLinkCopiedAlert: () => void;
	copyMeetLinkToClipboard: (timeToHide: number) => void;
	sendPatientWaiting: () => Promise<void>;
	sendProfessionalJoined: () => Promise<void>;
	hangUpAndFinish: () => Promise<void>;
	getInsuranceBrand: (origin: string) => { primary: string; secondary: string; name: string } | undefined;
}

const MeetContext = createContext<MeetContextValue | undefined>(undefined);

export function MeetContextProvider({ children, repository, features }: Readonly<MeetContextProviderProps>) {
	const { i18n, t } = useTranslationsContext();

	const [meet, setMeet] = useSessionStoragePersistedState<Meet | undefined>('meet', undefined);
	const [professionalAvailable, setProfessionalAvailable] = useSessionStoragePersistedState('professionalAvailable', false);
	const [retrieveMeetError, setRetrieveMeetError] = useState<string | undefined>();
	const [refreshMeetError, setRefreshMeetError] = useState<string | undefined>();
	const [isCopiedMeetLinkToClipboard, setIsCopiedMeetLinkToClipboard] = useState(false);
	const [sentProfessionalJoinedCode, setSentProfessionalJoinedCode] = useSessionStoragePersistedState<string | undefined>('sentProfessionalJoinedCode', undefined);
	const [participantConnected, setParticipantConnected] = useState(false);

	const resetState = useCallback(() => {
		setMeet(undefined);
		setProfessionalAvailable(false);
		setRetrieveMeetError(undefined);
		setSentProfessionalJoinedCode(undefined);
	}, []);

	const retrieveMeet = useCallback(async (code: string, tenant?: string) => {
		resetState();
		try {
			const meet = await repository.loadMeet(code, tenant);
			if (meet.code !== sentProfessionalJoinedCode) {
				setSentProfessionalJoinedCode(undefined);
			}
			setMeet({ ...meet, code, tenant });
			publish(MeetDomainEvents.retrievedMeet, { meet: { ...meet, code, tenant } });
		} catch (error) {
			setRetrieveMeetError(t('errors.invalid_code'));
		}
	}, [repository, sentProfessionalJoinedCode]);

	const refreshMeet = useCallback(async () => {
		if (!meet) {
			return;
		}

		publish(MeetDomainEvents.refreshMeet);
		setRefreshMeetError(undefined);
		try {
			const refreshedMeet = await repository.loadMeet(meet.code, meet.tenant);
			setMeet({ ...refreshedMeet, code: meet.code, tenant: meet.tenant });
			publish(MeetDomainEvents.refreshedMeet, { meet: refreshedMeet });
		} catch (error) {
			setRefreshMeetError(t('errors.refresh_meet_failed'));
			publish(MeetDomainEvents.refreshMeetFailed);
		}
	}, [meet, repository]);

	const connectionCredentials = useMemo(() => {
		if (!meet) {
			return undefined;
		}

		return {
			apiKey: meet?.connection?.apiKey,
			sessionId: meet?.connection?.sessionId,
			token: meet?.connection?.token,
		};
	}, [meet]);

	const isProfessional = useMemo(() => {
		const professionalParticipant = meet?.participants.find(({ type }) => type.toLowerCase() === 'professional');

		return meet?.memberId === professionalParticipant?.memberId;
	}, [meet]);

	const patientData = useMemo(() => {
		return meet?.participants.find(({ type }) => type.toLowerCase() === 'patient');
	}, [meet]);

	const professionalData = useMemo(() => {
		return meet?.participants.find(({ type }) => type.toLowerCase() === 'professional');
	}, [meet]);

	const participantData = useMemo(() => {
		return isProfessional ? patientData : professionalData;
	}, [meet]);
	
	const specialty = useMemo(() => {
		if (!meet?.meta?.speciality) {
			return undefined;
		}

		const capitalizedLocale = capitalize(i18n.language === 'es' ? 'es' : 'en');
		return meet.meta.speciality[`name${capitalizedLocale}`];
	}, [meet]);

	const meetReadyForWaiting = useMemo(() => {
		if (!meet) {
			return false;
		}
		
		return meet.status === 'STARTED' || meet.status === 'NOT_STARTED';
	}, [meet]);

	const setProfessionalAvailability = useCallback(() => {
		setProfessionalAvailable(true);
	}, []);

	const participantJoined = useCallback((event: CustomEvent<Partial<Record<string, any>>>) => {
		const videoCallEvent = event.detail.event;

		if (videoCallEvent?.stream?.connection?.data?.includes('professional')) {
			publish(MeetDomainEvents.professionalAvailable);
		} else {
			publish(MeetDomainEvents.patientAvailable);
		}

		setParticipantConnected(true);
	}, []);

	const participantLeft = useCallback(() => {
		setParticipantConnected(false);
	}, []);

	const sendPatientWaiting = useCallback(async () => {
		if (!meet) {
			publish(MeetDomainEvents.cantSendPatientWaiting);
			return;
		}

		if (isProfessional) {
			return;
		}

		publish(MeetDomainEvents.patientWaitingSending);
		
		try {
			await repository.waiting(meet);
			publish(MeetDomainEvents.patientWaitingSent);
		} catch (error) {
			publish(MeetDomainEvents.patientWaitingSendingFailed);
		}
	}, [repository, meet]);

	const sendProfessionalJoined = useCallback(async () => {
		if (!meet) {
			publish(MeetDomainEvents.cantSendProfessionalJoined);
			return;
		}

		if (!isProfessional || !!sentProfessionalJoinedCode) {
			return;
		}

		publish(MeetDomainEvents.professionalJoinedSending);
		
		try {
			await repository.join(meet);
			publish(MeetDomainEvents.professionalJoinedSent);
			setSentProfessionalJoinedCode(meet.code);
		} catch (error) {
			publish(MeetDomainEvents.professionalJoinedSendingFailed);
		}
	}, [repository, meet, sentProfessionalJoinedCode]);

	const closeMeetLinkCopiedAlert = useCallback(() => {
		setIsCopiedMeetLinkToClipboard(false);
	}, []);

	const copyMeetLinkToClipboard = useCallback((timeToHide: number) => {
		if (isCopiedMeetLinkToClipboard) {
			return;
		}

		setIsCopiedMeetLinkToClipboard(true);

		setTimeout(() => {
			closeMeetLinkCopiedAlert();
		}, timeToHide);
	}, [isCopiedMeetLinkToClipboard]);

	const meetLink = useMemo(() => {
		if (!meet) {
			return undefined;
		}

		return meet.meta.videoLinkPatient;
	}, [meet]);

	const participantsNumber = useMemo(() => {
		if (!meet) {
			return 0;
		}

		return meet.participants.length;
	}, [meet]);

	const hangUpAndFinish = useCallback(async () => {
		if (!meet) {
			return;
		}

		publish(MeetDomainEvents.hangUpAndFinish);

		try {
			await repository.hangUpAndFinish(meet);

			publish(MeetDomainEvents.hangedUpAndFinished);
		} catch (e) {
			publish(MeetDomainEvents.hangUpAndFinishFailed, {
				error: e,
			});
		}
	}, [meet]);

	const insuranceBrands = useMemo(() => {
		return INSURANCE_BRANDS;
	}, [meet]);

	const getInsuranceBrand = useCallback((origin: string) => {
		return insuranceBrands?.[origin];
	}, [insuranceBrands]);

	useEffect(() => {
		subscribe(VideoCallEvents.participantJoined, participantJoined);
		subscribe(VideoCallEvents.participantLeft, participantLeft);
		subscribe(MeetDomainEvents.professionalAvailable, setProfessionalAvailability);

		return () => {
			unsubscribe(VideoCallEvents.participantJoined, participantJoined);
			unsubscribe(VideoCallEvents.participantLeft, participantLeft);
			unsubscribe(MeetDomainEvents.professionalAvailable, setProfessionalAvailability);
			resetState();
		};
	}, []);


	const value = useMemo(() => {
		return {
			meet,
			meetReadyForWaiting,
			meetLink,
			isProfessional,
			patientData,
			professionalData,
			participantData,
			specialty,
			professionalAvailable,
			retrieveMeetError,
			refreshMeetError,
			features,
			connectionCredentials,
			isCopiedMeetLinkToClipboard,
			participantsNumber,
			participantConnected,
			closeMeetLinkCopiedAlert,
			copyMeetLinkToClipboard,
			retrieveMeet,
			sendPatientWaiting,
			sendProfessionalJoined,
			hangUpAndFinish,
			refreshMeet,
			getInsuranceBrand
		};
	}, [meet, meetReadyForWaiting, meetLink, isProfessional, patientData, professionalData, participantData, specialty, professionalAvailable, retrieveMeetError, refreshMeetError, features, connectionCredentials, isCopiedMeetLinkToClipboard, participantsNumber, participantConnected]);

	return (
		<MeetContext.Provider value={value}>
			{children}
		</MeetContext.Provider>
	);
}

export const useMeetContext = () => {
	const meetContext = useContext(MeetContext);
	if (meetContext === undefined) {
		throw new Error('useMeetContext must be inside a MeetContextProvider');
	}

	return meetContext;
};
